import React, { useEffect, useState } from 'react';
import { Download, Loader2, RefreshCw, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { formatEuro, formatNumber } from '../../../utils/formatters';
import { BOLLETTO_LOGO_URL } from '../../../utils/constants';

/**
 * Header dell'analisi: barra scura Bolletto + riepilogo risparmio + selettore CTE + pulsante PDF + Finalizza.
 */
export const AnalysisSummaryHeader = ({
  analysis, isHistorical, downloading, onDownloadPdf,
  manualCte, onManualCteChange, availableCtes, updatingCte, onUpdateCte,
  isFinalized, finalizing, onFinalize,
  showCteInPdf = true, onToggleCteInPdf,
}) => {
  const annualPositive = analysis.annual_projection?.is_positive;
  const hasAnnual = !!analysis.annual_projection;
  const billPositive = analysis.is_positive;
  const showPositive = hasAnnual ? annualPositive : billPositive;
  const canEdit = !isHistorical && !isFinalized;
  const totalsInvalid = analysis.validation && analysis.validation.valid === false;
  const canFinalize = canEdit && !totalsInvalid;
  // Colore identità servizio: gas → azzurro scuro, energia → giallo ocra
  const isGas = analysis.competitor_data?.service_type === 'gas';
  const headerBg = isGas ? '#0B3A5C' : '#7A5B00';

  // Banner di conferma "Finalizzata" che scompare dopo 3s
  const [justFinalized, setJustFinalized] = useState(false);
  useEffect(() => {
    if (analysis.just_finalized_at) {
      setJustFinalized(true);
      const t = setTimeout(() => setJustFinalized(false), 3000);
      return () => clearTimeout(t);
    }
  }, [analysis.just_finalized_at]);

  return (
    <>
      {/* PDF Header - barra scura con Bolletto (colore in base al servizio) */}
      <div className="px-6 py-5 flex items-center" style={{ background: headerBg }} data-testid={`analysis-header-${isGas ? 'gas' : 'energia'}`}>
        <img src={BOLLETTO_LOGO_URL} alt="Bolletto" className="w-auto brightness-0 invert" style={{ height: '56px' }} data-pdf-img />
      </div>

      {/* Riepilogo risparmio */}
      <div className={`p-6 ${showPositive ? 'bg-[#E8F5E9]' : 'bg-orange-50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <SavingsHeadline analysis={analysis} hasAnnual={hasAnnual} annualPositive={annualPositive} billPositive={billPositive} />
          </div>
          <div className="flex items-center gap-2 print:hidden">
            {canEdit && onFinalize && (
              <button
                onClick={onFinalize}
                disabled={finalizing || !canFinalize}
                className="flex items-center gap-2 text-sm py-2 px-4 h-fit rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                data-testid="finalize-btn"
                title={canFinalize ? 'Salva analisi nello storico (la bolletta resta modificabile)' : 'Impossibile finalizzare: totale bolletta non coerente'}
              >
                {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Finalizza
              </button>
            )}
            {justFinalized && (
              <span className="text-xs text-[#4CAF50] font-medium flex items-center gap-1 animate-pulse" data-testid="finalized-toast">
                <CheckCircle2 className="w-3.5 h-3.5" /> Salvata nello storico!
              </span>
            )}
            <button
              onClick={onDownloadPdf}
              disabled={downloading}
              className="el-btn-primary flex items-center gap-2 text-sm py-2 px-4 h-fit"
              data-testid="download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Scarica PDF
            </button>
          </div>
        </div>

        {/* Selettore CTE manuale — solo se modificabile (non finalizzata e non storica) */}
        {canEdit && (
          <div className="mt-3 flex items-center gap-3 print:hidden" data-testid="manual-cte-selector">
            <label className="text-sm text-[#5C6E5C] whitespace-nowrap">Cambia CTE:</label>
            <select
              value={manualCte}
              onChange={(e) => onManualCteChange(e.target.value)}
              className="el-input py-1.5 px-3 text-sm flex-1 max-w-[200px]"
              data-testid="manual-cte-select"
            >
              {availableCtes.map((cte) => (
                <option key={cte.code} value={cte.code}>
                  {cte.code} (Spread: {cte.spread} | Fissa: {cte.quota_fissa}€)
                </option>
              ))}
            </select>
            <button
              onClick={onUpdateCte}
              disabled={updatingCte || manualCte === analysis.selected_cte}
              className="flex items-center gap-1.5 py-1.5 px-4 rounded-xl text-sm font-medium bg-[#4CAF50] text-white hover:bg-[#3D8C40] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="update-cte-btn"
            >
              {updatingCte ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Aggiorna
            </button>
            {onToggleCteInPdf && (
              <button
                onClick={onToggleCteInPdf}
                className="p-1.5 rounded-xl border border-[#DCE3DC] bg-white hover:bg-[#F4F7F4] transition-colors"
                title={showCteInPdf ? 'Nascondi codice CTE nel PDF' : 'Mostra codice CTE nel PDF'}
                data-testid="toggle-cte-visibility-btn"
                aria-pressed={showCteInPdf}
              >
                {showCteInPdf
                  ? <Eye className="w-4 h-4 text-[#4CAF50]" />
                  : <EyeOff className="w-4 h-4 text-[#5C6E5C]" />}
              </button>
            )}
          </div>
        )}
        {isHistorical && (
          <div className="mt-2 print:hidden" data-testid="frozen-badge">
            <span className="inline-flex items-center gap-1 text-xs text-[#5C6E5C] bg-[#F4F7F4] px-2 py-1 rounded-full">
              Analisi nello storico (sola lettura)
            </span>
          </div>
        )}
      </div>
    </>
  );
};

/** Titolo risparmio/rincaro — logica condizionale isolata */
const SavingsHeadline = ({ analysis, hasAnnual, annualPositive, billPositive }) => {
  if (hasAnnual && annualPositive) {
    return (
      <>
        <h2 className="text-2xl font-medium text-[#4CAF50]">
          Risparmio Annuo di {formatEuro(Math.abs(analysis.annual_projection.savings_annual))}!
        </h2>
        <p className="text-[#5C6E5C] mt-1" style={{ lineHeight: '1.5' }}>
          {billPositive
            ? <>Con <strong style={{ color: '#4CAF50' }}>Energy.Lab</strong> risparmi {formatEuro(Math.abs(analysis.savings_amount))} su questa bolletta e <strong className="text-[#4CAF50]">&nbsp;{formatEuro(Math.abs(analysis.annual_projection.savings_annual))}/anno</strong></>
            : <>Su questa bolletta Energy.Lab costa {formatEuro(Math.abs(analysis.savings_amount))} in più, ma <strong className="text-[#4CAF50]">su 12 mesi risparmi {formatNumber(Math.abs(analysis.annual_projection.savings_annual_percent), 1)}%</strong></>
          }
        </p>
      </>
    );
  }
  if (hasAnnual && !annualPositive) {
    return (
      <>
        <h2 className="text-2xl font-medium text-orange-600">
          Rincaro Annuo di {formatEuro(Math.abs(analysis.annual_projection.savings_annual))}
        </h2>
        <p className="text-[#5C6E5C] mt-1">
          {billPositive
            ? <>Su questa bolletta Energy.Lab fa risparmiare {formatEuro(Math.abs(analysis.savings_amount))}, ma <strong className="text-orange-600">su 12 mesi costa {formatNumber(Math.abs(analysis.annual_projection.savings_annual_percent), 1)}% in più</strong></>
            : <>La migliore offerta Energy.Lab non supera il concorrente su base annua.</>
          }
        </p>
      </>
    );
  }
  if (billPositive) {
    return (
      <>
        <h2 className="text-2xl font-medium text-[#4CAF50]">Risparmio del {formatNumber(analysis.savings_percent, 1)}%!</h2>
        <p className="text-[#5C6E5C] mt-1" style={{ lineHeight: '1.5' }}>
          Con <strong style={{ color: '#4CAF50' }}>Energy.Lab</strong> risparmi {formatEuro(analysis.savings_amount)}
        </p>
      </>
    );
  }
  return (
    <>
      <h2 className="text-2xl font-medium text-orange-600">Nessun Risparmio Disponibile</h2>
      <p className="text-[#5C6E5C] mt-1">La migliore offerta Energy.Lab non supera il concorrente.</p>
    </>
  );
};
